import { useEffect, useRef } from 'react'
import { useControls } from 'react-zoom-pan-pinch'
import getSteppedZoom from './getSteppedZoom'

interface IUseSteppedWheelZoom {
  step: number
  minZoom: number
  maxZoom: number
  /** Chamado a cada `wheel` sem `Ctrl`/`⌘`, que é deixado passar para rolar a página. */
  onWheelWithoutModifier?: () => void
}

// Um notch de mouse costuma gerar ~100px; trackpads geram vários eventos
// pequenos, que são acumulados até completar um degrau.
const WHEEL_DELTA_THRESHOLD = 50
const LINE_HEIGHT_PX = 16
const PAGE_HEIGHT_PX = 800

const toPixels = (event: WheelEvent) => {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return event.deltaY * LINE_HEIGHT_PX
  }
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return event.deltaY * PAGE_HEIGHT_PX
  }
  return event.deltaY
}

/**
 * Substitui o zoom contínuo da roda do mouse da `react-zoom-pan-pinch` por
 * degraus de `step`, ancorados no cursor. Só age com `Ctrl`/`⌘` pressionado
 * (o pinch do trackpad também chega com `ctrlKey`), para a roda sozinha rolar
 * a página. Requer `wheel={{ disabled: true }}` no `TransformWrapper` e deve ser
 * usado dentro dele.
 */
const useSteppedWheelZoom = ({
  step,
  minZoom,
  maxZoom,
  onWheelWithoutModifier
}: IUseSteppedWheelZoom) => {
  const { instance, zoomToPoint } = useControls()
  // Refs porque o listener é re-registrado a cada render (zoomToPoint muda)
  const accumulatedDelta = useRef(0)
  const isAnimating = useRef(false)

  useEffect(() => {
    const wrapper = instance.wrapperComponent
    if (!wrapper) return

    const handleWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) {
        accumulatedDelta.current = 0
        onWheelWithoutModifier?.()
        return
      }

      event.preventDefault()
      if (isAnimating.current) return

      accumulatedDelta.current += toPixels(event)
      if (Math.abs(accumulatedDelta.current) < WHEEL_DELTA_THRESHOLD) return

      const direction = accumulatedDelta.current < 0 ? 'in' : 'out'
      accumulatedDelta.current = 0

      const { scale } = instance.state
      const target = getSteppedZoom(scale, direction, step, minZoom, maxZoom)
      if (target === scale) return

      isAnimating.current = true
      void zoomToPoint(target, event.clientX, event.clientY).finally(() => {
        isAnimating.current = false
      })
    }

    wrapper.addEventListener('wheel', handleWheel, { passive: false })
    return () => wrapper.removeEventListener('wheel', handleWheel)
  }, [instance, zoomToPoint, step, minZoom, maxZoom, onWheelWithoutModifier])
}

export default useSteppedWheelZoom
